import supabase from "@/lib/supabase";

/**
 * Fetch a user's profile by ID
 * @param {string} userId - The user's UUID
 * @returns {Promise<Object>} The user profile data
 */
export const getProfileById = async (userId) => {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching profile:", error.message);
    throw error;
  }
};

/**
 * Update a user's profile
 * @param {string} userId - The user's UUID
 * @param {Object} updates - The profile fields to update
 * @returns {Promise<Object>} The updated profile data
 */
export const updateProfile = async (userId, updates) => {
  try {
    // Validate required fields
    if (!userId) throw new Error("User ID is required");
    if (!updates || Object.keys(updates).length === 0) {
      throw new Error("No update data provided");
    }

    // Extract valid direct fields from updates
    const {
      full_name,
      email,
      avatar_url,
      onboarding_completed,
      user_workspaces,
      ...otherFields
    } = updates;

    // Create valid update object with only fields that exist in the profiles table
    const validUpdates = {
      updated_at: new Date().toISOString(),
    };

    // Add valid fields if they exist
    if (full_name !== undefined) validUpdates.full_name = full_name;
    if (email !== undefined) validUpdates.email = email;
    if (avatar_url !== undefined) validUpdates.avatar_url = avatar_url;
    if (onboarding_completed !== undefined)
      validUpdates.onboarding_completed = onboarding_completed;
    if (user_workspaces !== undefined)
      validUpdates.user_workspaces = user_workspaces;

    // If there are other fields to update, merge them with existing settings
    if (Object.keys(otherFields).length > 0) {
      // Get existing settings
      const { data: existingProfile, error: fetchError } = await supabase
        .from("profiles")
        .select("settings")
        .eq("id", userId)
        .single();

      if (fetchError) throw fetchError;

      const currentSettings = existingProfile?.settings || {};

      // Store other fields in the profile section of settings
      validUpdates.settings = {
        ...currentSettings,
        profile: {
          ...(currentSettings.profile || {}),
          ...otherFields,
        },
      };
    }

    const { data, error } = await supabase
      .from("profiles")
      .update(validUpdates)
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating profile:", error.message);
    throw error;
  }
};

/**
 * Updates user preferences in profile settings
 * @param {string} userId - The ID of the user
 * @param {Object} preferences - Preferences to update
 * @returns {Promise<Object>} The updated profile
 */
export const updatePreferences = async (userId, preferences) => {
  try {
    if (!userId) throw new Error("User ID is required");
    if (!preferences || Object.keys(preferences).length === 0) {
      throw new Error("No preferences data provided");
    }

    // Get existing settings
    const { data: existingProfile, error: fetchError } = await supabase
      .from("profiles")
      .select("settings")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    const currentSettings = existingProfile?.settings || {};

    // Deep merge the preferences into the current settings
    // This properly handles nested objects instead of just flat preferences
    const updatedSettings = deepMerge(currentSettings, preferences);

    const { data, error } = await supabase
      .from("profiles")
      .update({
        settings: updatedSettings,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating preferences:", error.message);
    throw error;
  }
};

/**
 * Helper function to deep merge objects for settings
 * @param {Object} target - The target object
 * @param {Object} source - The source object to merge
 * @returns {Object} Merged object
 */
function deepMerge(target, source) {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          output[key] = source[key];
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        output[key] = source[key];
      }
    });
  }

  return output;
}

/**
 * Helper to check if value is an object
 * @param {any} item - Item to check
 * @returns {boolean} True if object
 */
function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item);
}

/**
 * Update user API key map
 * @param {string} userId - The user's UUID
 * @param {Object} apiKeyMap - The new API key map
 * @returns {Promise<Object>} The updated profile data
 */
export const updateApiKeyMap = async (userId, apiKeyMap) => {
  if (!userId) throw new Error("User ID is required");
  if (!apiKeyMap || Object.keys(apiKeyMap).length === 0) {
    throw new Error("No API key map data provided");
  }

  try {
    // Get existing settings
    const { data: existingProfile, error: fetchError } = await supabase
      .from("profiles")
      .select("settings")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    const currentSettings = existingProfile?.settings || {};

    // Create updated settings with the new API key map
    const updatedSettings = {
      ...currentSettings,
      personalization: {
        ...(currentSettings.personalization || {}),
        advanced: {
          ...(currentSettings.personalization?.advanced || {}),
          api_key_map: apiKeyMap,
        },
      },
    };

    // Update the settings in the database
    const { data, error } = await supabase
      .from("profiles")
      .update({
        settings: updatedSettings,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating API key map:", error.message);
    throw error;
  }
};

/**
 * Update user theme preference
 * @param {string} userId - The user's UUID
 * @param {string} theme - The new theme preference
 * @returns {Promise<Object>} The updated profile data
 */
export const updateThemePreference = async (userId, theme) => {
  try {
    if (!userId) throw new Error("User ID is required");
    if (!theme) throw new Error("Theme value is required");

    // Get existing settings
    const { data: existingProfile, error: fetchError } = await supabase
      .from("profiles")
      .select("settings")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    const currentSettings = existingProfile?.settings || {};

    // Update theme in system settings
    const updatedSettings = {
      ...currentSettings,
      system: {
        ...(currentSettings.system || {}),
        theme,
      },
    };

    const { data, error } = await supabase
      .from("profiles")
      .update({
        settings: updatedSettings,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating theme preference:", error.message);
    throw error;
  }
};

/**
 * Update user language preference
 * @param {string} userId - The user's UUID
 * @param {string} language - The new language preference
 * @returns {Promise<Object>} The updated profile data
 */
export const updateLanguagePreference = async (userId, language) => {
  try {
    if (!userId) throw new Error("User ID is required");
    if (!language) throw new Error("Language value is required");

    // Get existing settings
    const { data: existingProfile, error: fetchError } = await supabase
      .from("profiles")
      .select("settings")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    const currentSettings = existingProfile?.settings || {};

    // Update language in system settings
    const updatedSettings = {
      ...currentSettings,
      system: {
        ...(currentSettings.system || {}),
        language,
      },
    };

    const { data, error } = await supabase
      .from("profiles")
      .update({
        settings: updatedSettings,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating language preference:", error.message);
    throw error;
  }
};

/**
 * Update user's avatar URL
 * @param {string} userId - The user's UUID
 * @param {string} avatarUrl - The new avatar URL
 * @returns {Promise<Object>} The updated profile data
 */
export const updateAvatar = async (userId, avatarUrl) => {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .update({
        avatar_url: avatarUrl,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating avatar:", error.message);
    throw error;
  }
};

/**
 * Update user's onboarding status
 * @param {string} userId - The user's UUID
 * @param {boolean} hasOnboarded - The onboarding status
 * @returns {Promise<Object>} The updated profile data
 */
export const updateOnboardingStatus = async (userId, hasOnboarded) => {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .update({
        onboarding_completed: hasOnboarded,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating onboarding status:", error.message);
    throw error;
  }
};

/**
 * Update user's display name and full name
 * @param {string} userId - The user's UUID
 * @param {Object} params - The name parameters
 * @param {string} params.displayName - The new display name
 * @param {string} params.fullName - The new full name
 * @returns {Promise<Object>} The updated profile data
 */
export const updateNames = async (userId, { displayName, fullName }) => {
  try {
    if (!userId) throw new Error("User ID is required");
    if (!displayName && !fullName) {
      throw new Error("At least one name parameter is required");
    }

    // Initialize updates object
    const updates = {
      updated_at: new Date().toISOString(),
    };

    // Add full_name to updates if provided (this is a direct field in profiles table)
    if (fullName) updates.full_name = fullName;

    // If display name is provided, we need to update it in settings
    if (displayName) {
      // Get existing settings
      const { data: existingProfile, error: fetchError } = await supabase
        .from("profiles")
        .select("settings")
        .eq("id", userId)
        .single();

      if (fetchError) throw fetchError;

      const currentSettings = existingProfile?.settings || {};

      // Update display name in profile settings
      updates.settings = {
        ...currentSettings,
        profile: {
          ...(currentSettings.profile || {}),
          display_name: displayName,
        },
      };
    }

    const { data, error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating names:", error.message);
    throw error;
  }
};

/**
 * Update a user's settings
 * @param {string} userId - The user's UUID
 * @param {Object} settings - The settings object to update
 * @returns {Promise<Object>} The updated profile data
 */
export const updateSettings = async (userId, settings) => {
  try {
    if (!userId) throw new Error("User ID is required");
    if (!settings || Object.keys(settings).length === 0) {
      throw new Error("No settings data provided");
    }

    const { data: existingProfile, error: fetchError } = await supabase
      .from("profiles")
      .select("settings")
      .eq("id", userId)
      .single();

    if (fetchError) {
      console.error(
        "SupabaseProfileUtils: Error fetching existing profile",
        fetchError
      );
      throw fetchError;
    }

    if (!existingProfile) {
      console.error(
        "SupabaseProfileUtils: No existing profile found for user",
        userId
      );
      throw new Error("User profile not found");
    }

    // Merge existing settings with new settings using deep merge
    // Deep merge to handle nested settings
    const updatedSettings = deepMerge(existingProfile.settings || {}, settings);

    const { data, error } = await supabase
      .from("profiles")
      .update({
        settings: updatedSettings,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) {
      console.error("SupabaseProfileUtils: Error updating profile", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error(
      "SupabaseProfileUtils: Error updating settings:",
      error.message
    );
    throw error;
  }
};

export const SupabaseProfileUtils = {
  getProfileById,
  updateProfile,
  updatePreferences,
  updateThemePreference,
  updateLanguagePreference,
  updateAvatar,
  updateOnboardingStatus,
  updateNames,
  updateSettings,
};

export default SupabaseProfileUtils;
